import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';

/**
 * Visual debugging utilities for curve detection
 * Can be easily toggled on/off via DEBUG_ENABLED flag
 */

// Global debug flag - set to false to disable all debug visualization
export const DEBUG_ENABLED = true;

// Store debug objects for cleanup
let debugObjects = [];

/**
 * Clear all debug visualizations from the scene
 * @param {THREE.Scene} scene - The Three.js scene
 */
export function clearDebugVisualization(scene) {
  if (!scene) return;

  debugObjects.forEach(obj => {
    if (obj.parent) {
      obj.parent.remove(obj);
    }
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) obj.material.dispose();
  });

  debugObjects = [];
}

/**
 * Visualize detected curve segments with thick red lines (Line2)
 * @param {THREE.Scene} scene - The Three.js scene
 * @param {Array} segments - Array of segment objects from detectProblematicSegments
 * @param {Float32Array} positions - Position attribute array
 * @param {Object} options - Visualization options
 */
export function visualizeCurveSegments(scene, segments, positions, options = {}) {
  if (!DEBUG_ENABLED) {
    console.log('[CurveDebug] Debug visualization is disabled (DEBUG_ENABLED = false)');
    return;
  }

  if (!scene) {
    console.warn('[CurveDebug] No scene provided for visualization');
    return;
  }

  if (!segments || !positions) {
    console.warn('[CurveDebug] Missing segments or positions data');
    return;
  }

  const {
    curveColor = 0xff0000,        // Red for detected curves
    curveLineWidth = 5,            // Thick line width
    startMarkerColor = 0x0000ff,   // Blue for start
    endMarkerColor = 0xff00ff,     // Magenta for end
    markerSize = 0.1,
    showMarkers = true
  } = options;

  console.log(`[CurveDebug] Visualizing ${segments.length} curve segments with Line2`);

  segments.forEach((segment, idx) => {
    const { vertices } = segment;

    if (vertices.length < 2) return;

    // Extract 3D positions for all vertices in the curve
    const curvePoints = [];
    vertices.forEach(vertexIdx => {
      curvePoints.push(
        positions[vertexIdx * 3],
        positions[vertexIdx * 3 + 1],
        positions[vertexIdx * 3 + 2]
      );
    });

    // Create thick line using Line2
    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions(curvePoints);

    const lineMaterial = new LineMaterial({
      color: curveColor,
      linewidth: curveLineWidth, // In pixels
      resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
      depthTest: true,
      transparent: true,
      opacity: 0.8
    });

    const line = new Line2(lineGeometry, lineMaterial);
    line.computeLineDistances();
    line.name = `CurveDebug_Segment${idx}_Line`;
    line.renderOrder = 999; // Render on top
    scene.add(line);
    debugObjects.push(line);

    // Optional: Add start/end markers
    if (showMarkers) {
      const startVertexIdx = vertices[0];
      const endVertexIdx = vertices[vertices.length - 1];

      const startPos = new THREE.Vector3(
        positions[startVertexIdx * 3],
        positions[startVertexIdx * 3 + 1],
        positions[startVertexIdx * 3 + 2]
      );

      const endPos = new THREE.Vector3(
        positions[endVertexIdx * 3],
        positions[endVertexIdx * 3 + 1],
        positions[endVertexIdx * 3 + 2]
      );

      // Create start marker (blue sphere)
      const startMarker = createDebugSphere(startPos, markerSize, startMarkerColor);
      startMarker.name = `CurveDebug_Segment${idx}_Start`;
      scene.add(startMarker);
      debugObjects.push(startMarker);

      // Create end marker (magenta sphere)
      const endMarker = createDebugSphere(endPos, markerSize, endMarkerColor);
      endMarker.name = `CurveDebug_Segment${idx}_End`;
      scene.add(endMarker);
      debugObjects.push(endMarker);
    }

    // Calculate curve statistics
    let curveLength = 0;
    for (let i = 1; i < vertices.length; i++) {
      const v1Idx = vertices[i - 1];
      const v2Idx = vertices[i];
      const v1 = new THREE.Vector3(
        positions[v1Idx * 3],
        positions[v1Idx * 3 + 1],
        positions[v1Idx * 3 + 2]
      );
      const v2 = new THREE.Vector3(
        positions[v2Idx * 3],
        positions[v2Idx * 3 + 1],
        positions[v2Idx * 3 + 2]
      );
      curveLength += v1.distanceTo(v2);
    }

    console.log(
      `[CurveDebug] Segment ${idx}: ${vertices.length} vertices, ` +
      `length: ${curveLength.toFixed(2)} units, ` +
      `indices: ${segment.startIdx}-${segment.endIdx}`
    );
  });
}

/**
 * Visualize the entire boundary loop with color-coded vertices based on upward scores
 * @param {THREE.Scene} scene - The Three.js scene
 * @param {Array} boundaryLoop - Boundary loop vertex indices
 * @param {Float32Array} positions - Position attribute array
 * @param {Array} curvatureAnalysis - Analysis data from analyzeBoundaryCurvature
 */
export function visualizeBoundaryWithScores(scene, boundaryLoop, positions, curvatureAnalysis) {
  if (!DEBUG_ENABLED || !scene || !boundaryLoop || !positions) return;

  console.log(`[CurveDebug] Visualizing boundary loop with ${boundaryLoop.length} vertices`);

  // Create boundary line
  const boundaryPoints = [];
  boundaryLoop.forEach(vertexIdx => {
    boundaryPoints.push(
      positions[vertexIdx * 3],
      positions[vertexIdx * 3 + 1],
      positions[vertexIdx * 3 + 2]
    );
  });

  // Close the loop
  const firstVertexIdx = boundaryLoop[0];
  boundaryPoints.push(
    positions[firstVertexIdx * 3],
    positions[firstVertexIdx * 3 + 1],
    positions[firstVertexIdx * 3 + 2]
  );

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(boundaryPoints);

  const lineMaterial = new LineMaterial({
    color: 0xffff00, // Yellow for boundary
    linewidth: 2,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    transparent: true,
    opacity: 0.5
  });

  const boundaryLine = new Line2(lineGeometry, lineMaterial);
  boundaryLine.computeLineDistances();
  boundaryLine.name = 'BoundaryDebug_Loop';
  scene.add(boundaryLine);
  debugObjects.push(boundaryLine);

  // Add color-coded vertex markers if analysis is provided
  if (curvatureAnalysis) {
    boundaryLoop.forEach((vertexIdx, i) => {
      const pos = new THREE.Vector3(
        positions[vertexIdx * 3],
        positions[vertexIdx * 3 + 1],
        positions[vertexIdx * 3 + 2]
      );

      const score = curvatureAnalysis[i].upwardScore;

      // Color based on upward score
      let color;
      if (score < 0.3) {
        color = 0xff0000; // Red for problematic
      } else if (score < 0.6) {
        color = 0xffff00; // Yellow for transition
      } else {
        color = 0x00ff00; // Green for good
      }

      const sphere = createDebugSphere(pos, 0.15, color);
      sphere.name = `BoundaryDebug_Vertex${i}`;
      scene.add(sphere);
      debugObjects.push(sphere);
    });
  }
}

/**
 * Visualize the straight line replacement for a curve
 * @param {THREE.Scene} scene - The Three.js scene
 * @param {Array} straightVertices - Array of Vector3 for straight line
 * @param {number} segmentIdx - Index of the segment
 */
export function visualizeStraightLine(scene, straightVertices, segmentIdx) {
  if (!DEBUG_ENABLED || !scene || !straightVertices) return;

  // Create points array for Line2
  const points = [];
  straightVertices.forEach(vertex => {
    points.push(vertex.x, vertex.y, vertex.z);
  });

  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(points);

  const lineMaterial = new LineMaterial({
    color: 0x00ffff, // Cyan for straight replacement
    linewidth: 4,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
    dashed: true,
    dashScale: 2,
    dashSize: 0.5,
    gapSize: 0.5
  });

  const line = new Line2(lineGeometry, lineMaterial);
  line.computeLineDistances();
  line.name = `StraightLine_Segment${segmentIdx}`;
  scene.add(line);
  debugObjects.push(line);

  console.log(`[CurveDebug] Visualized straight line replacement for segment ${segmentIdx} (${straightVertices.length} vertices)`);
}

/**
 * Helper function to create a debug sphere
 * @param {THREE.Vector3} position - Position for the sphere
 * @param {number} radius - Sphere radius
 * @param {number} color - Sphere color
 * @returns {THREE.Mesh} The created sphere mesh
 */
function createDebugSphere(position, radius, color) {
  const geometry = new THREE.SphereGeometry(radius, 16, 16);
  const material = new THREE.MeshBasicMaterial({
    color: color,
    transparent: true,
    opacity: 0.9,
    depthTest: false
  });
  const sphere = new THREE.Mesh(geometry, material);
  sphere.position.copy(position);
  sphere.renderOrder = 1000; // Render on top
  return sphere;
}

/**
 * Log detailed curve segment statistics to console
 * @param {Array} segments - Array of detected segments
 * @param {Float32Array} positions - Position attribute array
 */
export function logSegmentStatistics(segments, positions) {
  if (!DEBUG_ENABLED || !segments) return;

  console.group('[CurveDebug] Detailed Segment Statistics');

  segments.forEach((seg, idx) => {
    const avgScore = seg.scores.reduce((sum, s) => sum + s, 0) / seg.scores.length;
    const minScore = Math.min(...seg.scores);
    const maxScore = Math.max(...seg.scores);

    // Calculate segment length in 3D space
    let segmentLength = 0;
    for (let i = 1; i < seg.vertices.length; i++) {
      const v1Idx = seg.vertices[i - 1];
      const v2Idx = seg.vertices[i];
      const v1 = new THREE.Vector3(
        positions[v1Idx * 3],
        positions[v1Idx * 3 + 1],
        positions[v1Idx * 3 + 2]
      );
      const v2 = new THREE.Vector3(
        positions[v2Idx * 3],
        positions[v2Idx * 3 + 1],
        positions[v2Idx * 3 + 2]
      );
      segmentLength += v1.distanceTo(v2);
    }

    console.log(
      `Segment ${idx}:`,
      `\n  Vertices: ${seg.vertices.length}`,
      `\n  3D Length: ${segmentLength.toFixed(2)} units`,
      `\n  Upward Scores: avg=${avgScore.toFixed(3)}, min=${minScore.toFixed(3)}, max=${maxScore.toFixed(3)}`,
      `\n  Boundary Indices: ${seg.startIdx} to ${seg.endIdx}`,
      `\n  Vertex IDs: [${seg.vertices.slice(0, 3).join(', ')}${seg.vertices.length > 3 ? '...' : ''}]`
    );
  });

  console.groupEnd();
}

/**
 * Update Line2 resolution on window resize
 * Call this from a window resize handler
 */
export function updateDebugLineResolution() {
  if (!DEBUG_ENABLED) return;

  const resolution = new THREE.Vector2(window.innerWidth, window.innerHeight);

  debugObjects.forEach(obj => {
    if (obj instanceof Line2 && obj.material instanceof LineMaterial) {
      obj.material.resolution = resolution;
    }
  });
}
